import gql from "graphql-tag";
import { useRef } from "react";
import { useInView } from "motion/react";
import { AnimVals } from "@/utils/animUtils";
import { useViewSize } from '@/providers/ViewSizeContext';
import { Container, SectionHeader } from "@components";
import { ACFMediaFragment } from "@fragments"
import { EventFragment } from "@/fragments/Event";
import { ExhibitTypeFragment } from "@/fragments/Exhibit";
import { useDebug } from '@/utils/useDebugLog';
import { FeatureFragment } from "@graphqlTypes";

import FeatureItem, { type FeatureItemType } from "./FeatureItem";

import className from "classnames/bind";
import styles from "./Feature.module.scss";
const cx = className.bind(styles);

type FeatureProps = Omit<FeatureFragment, '__typename' | 'items'> & {
  items?: Omit<FeatureFragment['items'][number], '__typename'>[];
}

export default function Feature(props: FeatureProps) {
  const { log } = useDebug({ name: 'Feature', bgColor: 'lime', enabled: false });

  const { items, float, headline, subhead, disableParallax } = props;
  const featureType = items.length > 2 ? "bento" : "columns";
  const showHeader = Boolean(headline || subhead);

  // -------------------- Animation

  const rootEl = useRef<HTMLDivElement>(null);

  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  log('Render', featureType, items.length, rootEl.current);

  // -------------------- Render

  return (
    <section className={cx("feature")} ref={rootEl}>
      <Container>
        {showHeader && (
          <SectionHeader
            headline={headline}
            subhead={subhead}
            subheadReveal
            isRevealEnabled
            isInView={isInView}
          />
        )}
        <div className={cx(["feature-row", "row", featureType, { "is-odd": (items.length % 2) !== 0 }, { float }])}>
          {items?.map((item: FeatureItemType, index) => {
            return (
              <FeatureItem
                single={((items.length === 1) || (items.length > 2 && index === 0))}
                featureType={featureType}
                data={item}
                index={index}
                disableParallax={disableParallax}
                key={index}
              />
            );
          })}
        </div>
      </Container>
    </section>
  );
}

Feature.fragments = {
  entry: gql`
    ${ACFMediaFragment}
    ${EventFragment}
    ${ExhibitTypeFragment}
    fragment Feature on FlexiblePageContentContentFeatureLayout {
      headline
      subhead
      float
      disableParallax
      items {
        hideTypePill
        body
        ctaLabel
        headline
        linkType
        sectionId
        pillLabel
        image {
          ...ACFMedia
        }
        externalLink
        page {
          node {
            __typename
            uri
            ... on Exhibit {
              ...ExhibitType
            }
            ... on Event {
              ...EventFragment
            }
          }
        }
      }
    }
  `
};
